
import { SEO } from '../../utils/seo';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const commonProblems = [
  {
    icon: 'ri-bar-chart-horizontal-line',
    title: 'Striping from Inconsistent Speed or Altitude',
    description: 'When drones vary speed or altitude mid-pass, application rates fluctuate. The result is visible banding across the field—some areas overdosed, others undertreated. This inconsistency compounds through the growing season.',
    severity: 'high'
  },
  {
    icon: 'ri-layout-row-line',
    title: 'Missed Passes and Uneven Overlap',
    description: 'Poor flight planning or GPS drift leads to gaps between passes. Weeds survive in missed strips. Disease pressure builds in untreated zones. By the time you notice, the damage is done.',
    severity: 'high'
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Poor Calibration and Flow Rate Errors',
    description: 'Nozzle calibration requires precision. When flow rates are off—even by 10%—you\'re either wasting product or leaving crops vulnerable. Many operators skip pre-flight calibration checks entirely.',
    severity: 'high'
  },
  {
    icon: 'ri-windy-line',
    title: 'Drift from Improper Wind Assessment',
    description: 'Wind conditions change constantly. Operators who don\'t monitor wind speed and direction throughout the application risk drift onto neighboring fields, waterways, or sensitive areas. This creates liability and wastes product.',
    severity: 'medium'
  },
  {
    icon: 'ri-battery-low-line',
    title: 'Battery Swaps Causing Coverage Gaps',
    description: 'Every battery change is a potential coverage gap. Without proper waypoint marking and resumption protocols, operators lose track of where they stopped. The field looks covered, but strips remain untreated.',
    severity: 'medium'
  },
  {
    icon: 'ri-user-unfollow-line',
    title: 'Operators Without Ag Experience',
    description: 'Flying a drone is one skill. Understanding crop conditions, growth stages, and application timing is another. Operators who can\'t read the field often apply at the wrong time or miss early signs of problems.',
    severity: 'medium'
  },
  {
    icon: 'ri-file-unknow-line',
    title: 'Lack of Documentation or Follow-Up',
    description: 'No flight logs. No coverage maps. No post-application check. When something goes wrong, there\'s no record to review. When regulators ask questions, there\'s nothing to show.',
    severity: 'high'
  }
];

const costFactors = [
  {
    label: 'Yield Loss',
    impact: '5-15% reduction',
    description: 'Untreated or undertreated areas directly reduce harvest. A single missed fungicide window during disease pressure can cost more than the entire application.'
  },
  {
    label: 'Reapplication Costs',
    impact: '2x expense',
    description: 'When the first pass fails, you pay twice—once for the failed application, again to fix it. Plus the additional product cost and lost time.'
  },
  {
    label: 'Lost Timing Windows',
    impact: 'Irreversible',
    description: 'Some growth stages only happen once. Miss the optimal spray window and no amount of reapplication can recover the lost opportunity.'
  },
  {
    label: 'Added Stress',
    impact: 'Compounding',
    description: 'Crops under stress from poor application are more vulnerable to secondary problems. One mistake cascades into multiple issues throughout the season.'
  }
];

const ourApproach = [
  {
    step: 1,
    title: 'Field Verification',
    description: 'We walk the field before we fly it. Boundaries, obstacles, sensitive areas—all confirmed before the first battery is loaded.'
  },
  {
    step: 2,
    title: 'Mission Planning',
    description: 'Flight paths are planned for optimal coverage, accounting for field shape, wind patterns, and battery swap points. Nothing is left to chance.'
  },
  {
    step: 3,
    title: 'Calibration Protocol',
    description: 'Every application starts with flow rate verification. We confirm nozzle output matches the prescription before leaving the staging area.'
  },
  {
    step: 4,
    title: 'Weather Discipline',
    description: 'We monitor conditions continuously. If wind exceeds safe thresholds, we stop. No exceptions. Your field and your neighbors\' fields deserve that respect.'
  },
  {
    step: 5,
    title: 'Post-Application Validation',
    description: 'After every job, we review coverage maps and flight logs. Any anomalies are flagged and addressed before we leave the property.'
  },
  {
    step: 6,
    title: 'Clear Communication',
    description: 'You get documentation, coverage reports, and direct access to our team. Questions get answered. Problems get solved.'
  }
];

export default function DroneSprayingProblemsPage() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "When Drone Spraying Goes Wrong - Common Problems and How to Avoid Them",
    "description": "Learn about common failure modes in agricultural drone spraying and why execution quality matters more than price.",
    "author": {
      "@type": "Organization",
      "name": "Greenfield Partners"
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="When Drone Spraying Goes Wrong | Greenfield Partners"
        description="Learn about common failure modes in agricultural drone spraying—striping, missed passes, drift, and more. Understand why execution quality determines your yield."
        keywords="drone spraying problems, agricultural drone mistakes, spray drift, uneven application, drone operator experience"
        schema={schema}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1f28b4a6ee92da1065c98e3723e787aa.jpg"
            alt="Field showing uneven spray application patterns"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20">
          <div className="max-w-2xl">
            <span className="inline-block text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4">
              The Reality of Modern Ag Aviation
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              When Drone Spraying Goes Wrong
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Execution quality determines your yield—and your peace of mind. Here's what separates reliable operations from costly mistakes.
            </p>
            <div className="flex items-center gap-2 text-gray-300">
              <i className="ri-arrow-down-line text-xl animate-bounce"></i>
              <span className="text-sm">Explore the reality</span>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 px-6 bg-stone-100 topo-texture-light">
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 leading-snug">
            Drone spraying is everywhere.<br />
            <span className="text-emerald-800">Consistent, reliable execution is not.</span>
          </h2>
          <div className="text-lg text-gray-600 leading-relaxed space-y-6 text-left">
            <p>
              Agricultural drones have transformed how we protect crops. The technology is proven. The efficiency gains are real. But as adoption has accelerated, so has the gap between operators who understand agriculture and those who simply own equipment.
            </p>
            <p>
              We've seen the aftermath of poor execution—fields with visible striping, yield maps showing unexplained dips, growers frustrated by results that don't match promises. These problems aren't inevitable. They're preventable.
            </p>
            <p>
              This page isn't about fear. It's about understanding what can go wrong, why it matters, and how experienced operations prevent these issues before they cost you money.
            </p>
          </div>
          <div className="mt-12 h-px w-24 bg-emerald-700/30 mx-auto"></div>
        </div>
      </section>

      {/* Common Problems Section */}
      <section className="py-24 px-6 bg-stone-200/50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <span className="text-emerald-700 text-xs font-bold uppercase tracking-widest">Section 01</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Seven Problems We See Every Season
            </h2>
            <p className="text-gray-600 max-w-xl">
              These aren't hypotheticals. They're patterns we've observed across hundreds of fields—and issues we've been called in to fix. Not sure how to evaluate an operator? <a href="/operator-checklist" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Use our checklist tool <i className="ri-arrow-right-line"></i></a>
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commonProblems.map((problem, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow relative group"
              >
                {problem.severity === 'high' && (
                  <div className="absolute top-4 right-4 w-2 h-2 bg-amber-500 rounded-full"></div>
                )}
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-lg mb-4">
                  <i className={`${problem.icon} text-xl text-emerald-700`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug">
                  {problem.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {problem.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Evidence Section */}
      <section className="py-24 px-6 topo-cta-bg">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">Visual Proof</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              The Difference Is Visible
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Poor execution leaves evidence. These examples show what inconsistent application looks like—and what proper coverage should achieve.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Comparison 1: Striping */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
              <div className="relative h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=aerial%20drone%20photo%20of%20agricultural%20field%20showing%20visible%20striping%20pattern%20from%20uneven%20herbicide%20application%20alternating%20bands%20of%20healthy%20green%20crops%20and%20yellowing%20stressed%20plants%20clear%20evidence%20of%20missed%20passes%20professional%20agricultural%20documentation&width=600&height=400&seq=stripe001&orientation=landscape"
                  alt="Field showing striping from uneven application"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-red-500/90 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Poor Execution
                </div>
              </div>
              <div className="p-5">
                <h4 className="text-white font-semibold mb-2">Striping Pattern</h4>
                <p className="text-gray-400 text-sm">
                  Visible banding indicates inconsistent speed or altitude during application. Undertreated strips allow weeds or disease to persist.
                </p>
              </div>
            </div>

            {/* Comparison 2: Uniform Coverage */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
              <div className="relative h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=aerial%20drone%20photo%20of%20perfectly%20uniform%20green%20agricultural%20field%20with%20consistent%20crop%20health%20across%20entire%20area%20no%20striping%20or%20banding%20visible%20healthy%20vibrant%20green%20color%20throughout%20professional%20agricultural%20photography%20showing%20successful%20spray%20application&width=600&height=400&seq=uniform001&orientation=landscape"
                  alt="Field showing uniform healthy coverage"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-emerald-500/90 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Proper Coverage
                </div>
              </div>
              <div className="p-5">
                <h4 className="text-white font-semibold mb-2">Uniform Application</h4>
                <p className="text-gray-400 text-sm">
                  Consistent color and growth across the field indicates even coverage. No missed areas, no overdosed zones.
                </p>
              </div>
            </div>

            {/* Comparison 3: Missed Rows */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
              <div className="relative h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20crop%20field%20showing%20missed%20spray%20rows%20with%20visible%20weed%20growth%20in%20untreated%20strips%20green%20weeds%20growing%20between%20treated%20crop%20rows%20clear%20evidence%20of%20GPS%20drift%20or%20poor%20overlap%20agricultural%20documentation%20photo&width=600&height=400&seq=missed001&orientation=landscape"
                  alt="Field showing missed rows with weed growth"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-red-500/90 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Poor Execution
                </div>
              </div>
              <div className="p-5">
                <h4 className="text-white font-semibold mb-2">Missed Passes</h4>
                <p className="text-gray-400 text-sm">
                  Gaps between passes allow weeds to survive and compete with crops. Often caused by GPS drift or poor flight planning.
                </p>
              </div>
            </div>

            {/* Comparison 4: NDVI Style */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden">
              <div className="relative h-64">
                <img
                  src="https://readdy.ai/api/search-image?query=NDVI%20vegetation%20index%20map%20of%20agricultural%20field%20showing%20color%20gradient%20from%20red%20low%20health%20to%20green%20high%20health%20thermal%20imaging%20style%20crop%20health%20analysis%20visualization%20professional%20precision%20agriculture%20data%20display&width=600&height=400&seq=ndvi001&orientation=landscape"
                  alt="NDVI map showing crop health variation"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 bg-emerald-500/90 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Health Mapping
                </div>
              </div>
              <div className="p-5">
                <h4 className="text-white font-semibold mb-2">NDVI Analysis</h4>
                <p className="text-gray-400 text-sm">
                  Vegetation index mapping reveals application quality. Consistent green indicates uniform treatment; red zones show stress from missed or poor coverage.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Reality Section */}
      <section className="py-24 px-6 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Left Column - Dark */}
            <div className="lg:col-span-2 bg-gray-900 rounded-2xl p-8 lg:p-10">
              <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">The Hidden Cost</span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mt-3 mb-6">
                Why "Cheaper" Becomes Expensive
              </h2>
              <div className="text-gray-300 space-y-4 text-sm leading-relaxed">
                <p>
                  The lowest bid often comes from operators cutting corners—on calibration, on planning, on follow-through. The savings disappear when you factor in what goes wrong.
                </p>
                <p>
                  A $2/acre discount means nothing if you lose 8% of your yield to missed coverage. A fast turnaround is worthless if drift contaminates a neighbor's organic field.
                </p>
                <p>
                  The real cost of poor execution isn't the application fee. It's the opportunity cost of a compromised crop.
                </p>
              </div>
            </div>

            {/* Right Column - Cost Breakdown */}
            <div className="lg:col-span-3 space-y-4">
              {costFactors.map((factor, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-emerald-700 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg">{factor.label}</h4>
                      <p className="text-gray-600 text-sm mt-2 leading-relaxed">{factor.description}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="text-emerald-700 font-bold text-lg">{factor.impact}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-24 px-6 bg-stone-100 topo-texture-light">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-emerald-700 text-xs font-bold uppercase tracking-widest">Section 02</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              What Experienced Operations Do Differently
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These aren't trade secrets. They're the fundamentals that separate reliable service from risky shortcuts. <a href="/how-we-work" className="text-emerald-800 hover:text-emerald-700 font-semibold inline-flex items-center gap-1">Learn more about our process <i className="ri-arrow-right-line"></i></a>
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-emerald-200"></div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {ourApproach.map((item, index) => (
                <div
                  key={index}
                  className={`relative ${index % 2 === 1 ? 'lg:mt-24' : ''}`}
                >
                  {/* Step Number */}
                  <div className="w-12 h-12 bg-white border-3 border-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4 relative z-10 shadow-sm">
                    <span className="text-emerald-700 font-bold text-lg">{item.step}</span>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-sm text-center">
                    <h4 className="font-bold text-gray-900 text-lg mb-3">{item.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-24 px-6 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Left - Quote */}
            <div className="lg:col-span-2 relative">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-emerald-700 rounded-full"></div>
              <div className="pl-8">
                <blockquote className="text-2xl md:text-3xl font-bold text-gray-900 italic leading-snug mb-4">
                  "We don't just apply. We verify, document, and stand behind every pass."
                </blockquote>
                <p className="text-gray-500 text-sm">— Our Field Operations Standard</p>
              </div>
            </div>

            {/* Right - Content */}
            <div className="lg:col-span-3">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Built for the Long Term</h3>
              <div className="text-gray-600 space-y-4 leading-relaxed mb-8">
                <p>
                  We're not interested in one-off jobs where we never see the results. We work with growers season after season because we're invested in outcomes, not just invoices.
                </p>
                <p>
                  When something doesn't go as planned—and in agriculture, variables are constant—we're accountable. We review what happened, we communicate clearly, and we make it right.
                </p>
                <p>
                  That's not a marketing promise. It's how we've built relationships that last.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Pre-flight field verification',
                  'Post-application coverage reports',
                  'Complete flight documentation',
                  'Direct access to our team',
                  'Season-long partnership approach',
                  'Accountability when it matters'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center text-emerald-700">
                      <i className="ri-check-line text-lg"></i>
                    </div>
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20healthy%20uniform%20green%20agricultural%20field%20at%20golden%20hour%20sunset%20light%20casting%20warm%20glow%20over%20perfectly%20maintained%20crops%20rows%20extending%20to%20horizon%20professional%20agricultural%20landscape%20photography%20peaceful%20productive%20farmland&width=1920&height=800&seq=cta001&orientation=landscape"
            alt="Healthy uniform crop field"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <span className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4 block">
            Ready to Work Together?
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Price matters.<br />
            <span className="text-emerald-400">Execution matters more.</span>
          </h2>
          <p className="text-xl text-gray-200 mb-10 leading-relaxed">
            If you want it done right the first time, let's talk. We'll walk through your operation, understand your challenges, and show you what reliable service looks like. Want a quick estimate? <a href="/instant-quote" className="text-white hover:text-emerald-200 font-semibold underline">Get an instant quote</a>.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/#contact"
              className="px-8 py-4 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              Let's Talk About Your Operation
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="tel:+17179131780"
              className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer"
            >
              Or call us directly: 717-913-1780
            </a>
          </div>
        </div>
      </section>

      {/* Gradient bridge into footer */}
      <div className="h-24 bg-gradient-to-b from-black/70 to-[#1a1f1c]"></div>

      <Footer />
    </div>
  );
}
